import './UserTQConnectStats.scss';

import React, { memo } from 'react';

import { Typography } from 'antd';
import {
  PiStarFill,
  PiThumbsUpFill,
} from 'react-icons/pi';

import CustomPill from '../../../../components/CustomPill/CustomPill';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  useTQConnectUserProfileInfo,
} from '../../../../hooks/queries/tqConnectQueries/user';
import { numFormatterInLocale } from '../../../../shared/formatter';
import { useAppSelector } from '../../../../shared/hooks';
import { tintColor } from '../../../../shared/utils';
import {
  Onboarding,
  OnboardingStatus,
} from '../../../../types/userTypes';
import TQConnectScoreCard
  from '../../../TQConnect/components/TQConnectScoreCard/TQConnectScoreCard';
import { UserAccountType } from '../../types';

interface IUserTQConnectStatsProps {
  userId: string;
  userType: OnboardingStatus;
  viewerType: UserAccountType;
  isViewerSelf?: boolean;
}

const UserTQConnectStarCard: React.FC<IUserTQConnectStatsProps> = ({
  userId,
  userType,
  isViewerSelf,
}) => {
  const { colors: COLORS } = useTheme();

  const userDetails = useAppSelector((state) => state.user);

  const { data: profileInfo, isLoading } = useTQConnectUserProfileInfo(
    { userId, userType },
    true,
  );

  const STATS_COLORS = {
    reviews: {
      color: tintColor(COLORS.GREEN, 0),
      bgColor: '#fff',
    },
    shoutouts: {
      color: tintColor(COLORS.WARNING, 0),
      bgColor: '#fff',
    },
  };

  const renderStatRow = (
    type: 'reviews' | 'shoutouts',
    icon: (size: number, color: string) => React.ReactNode,
  ) => {
    const receivedValue =
      (type === 'reviews'
        ? profileInfo?.tqReviewReceived
        : profileInfo?.tqShoutoutReceived) || 0;
    const givenValue =
      (type === 'reviews'
        ? profileInfo?.tqReviewGiven
        : profileInfo?.tqShoutoutGiven) || 0;

    const label = type === 'reviews' ? 'Reviews' : 'Shoutouts';

    return (
      <div key={type} className="tq-star-card__stat-row">
        <CustomPill
          text={icon(18, STATS_COLORS[type].color)}
          color={STATS_COLORS[type].color}
          bgColor={STATS_COLORS[type].bgColor}
          width={32}
          height={32}
          shape="circle"
          border={0}
        />
        <div className="tq-star-card__stat-content">
          <Typography.Text className="tq-star-card__stat-title" strong>
            {label}
          </Typography.Text>
          <Typography.Text
            className="tq-star-card__stat-details"
            type="secondary">
            <>
              <span className="tq-star-card__stat-num">
                {numFormatterInLocale(receivedValue, undefined, 'SHORT')}
              </span>
              {' received · '}
              <>
                {userDetails.type === Onboarding.CREATOR_COMPLETED ||
                userDetails.type === Onboarding.CREATOR_RESTRICTED ||
                userId === userDetails?.id ? (
                  <>
                    <span className="tq-star-card__stat-num">
                      {numFormatterInLocale(givenValue, undefined, 'SHORT')}
                    </span>
                    {' given'}
                  </>
                ) : null}
              </>
            </>
          </Typography.Text>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="tq-connect-card__stats--loading">
        <Typography.Text>Loading...</Typography.Text>
      </div>
    );
  }

  if (!profileInfo) {
    return null;
  }

  return (
    <div className="tq-connect-star-card">
      <div className="tq-connect-star-card__gauge">
        <TQConnectScoreCard
          score={profileInfo.tqConnectScore}
          showInfoIcon={isViewerSelf}
          isOwnScore={isViewerSelf}
          variant="small"
        />
      </div>
      <div className="tq-connect-star-card__stats">
        {renderStatRow('reviews', (size) => (
          <PiThumbsUpFill size={size} style={{ flexShrink: 0 }} />
        ))}
        {renderStatRow('shoutouts', (size) => (
          <PiStarFill size={size} style={{ flexShrink: 0 }} />
        ))}
      </div>
    </div>
  );
};

export default memo(UserTQConnectStarCard);
